import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  Gift, Copy, Share2, Users, CheckCircle, Link as LinkIcon, 
  Mail, MessageCircle, Twitter, Linkedin, ArrowRight, Plus, Loader2
} from 'lucide-react';

const InviteFriend = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingLink, setCreatingLink] = useState(false);

  useEffect(() => {
    fetchInviteData();
  }, [token]);

  const fetchInviteData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/beta/invitations`, { headers });
      setInviteData(response.data);
    } catch (error) {
      console.error('Error fetching invite data:', error);
      if (error.response?.status === 403) {
        toast.error('Beta access required to invite friends');
      }
    } finally {
      setLoading(false);
    }
  };

  const createNewLink = async () => {
    setCreatingLink(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/beta/create-invite-link`, {}, { headers });
      toast.success('Invitation link created!');
      fetchInviteData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation link');
    } finally {
      setCreatingLink(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const shareVia = (platform, link) => {
    if (!link) return;
    
    const message = `Join me on Clierxter Vision Lab! Use my invitation link: ${link}`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
      email: `mailto:?subject=${encodeURIComponent('Join Clierxter Vision Lab Beta!')}&body=${encodeURIComponent(message)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Invite a Friend">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!inviteData) {
    return (
      <DashboardLayout title="Invite a Friend">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Gift className="text-[#52525B] mx-auto mb-6" size={64} />
          <h2 className="font-serif text-2xl text-white mb-4">Beta Access Required</h2>
          <p className="text-[#A1A1AA] mb-8">
            Join the Beta program to unlock the ability to invite friends.
          </p>
          <Button onClick={() => window.location.href = '/pricing'} className="btn-gold">
            Join Beta Now
            <ArrowRight className="ml-2" size={16} />
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Invite a Friend">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="card-premium p-6 text-center">
            <Users className="text-[#D4AF37] mx-auto mb-3" size={32} />
            <p className="text-3xl font-serif text-white mb-1">{inviteData.invitations_used}</p>
            <p className="text-[#52525B] text-sm">Friends Joined</p>
          </div>
          <div className="card-premium p-6 text-center">
            <LinkIcon className="text-[#D4AF37] mx-auto mb-3" size={32} />
            <p className="text-3xl font-serif text-white mb-1">{inviteData.invitations_created}</p>
            <p className="text-[#52525B] text-sm">Links Created</p>
          </div>
          <div className="card-premium p-6 text-center">
            <Gift className="text-[#D4AF37] mx-auto mb-3" size={32} />
            <p className="text-3xl font-serif text-white mb-1">{inviteData.invitations_remaining}</p>
            <p className="text-[#52525B] text-sm">Links Remaining</p>
          </div>
        </motion.div>

        {/* Create New Link */}
        {inviteData.can_create_more && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-8 text-center"
          >
            <Gift className="text-[#D4AF37] mx-auto mb-4" size={48} />
            <h2 className="font-serif text-2xl text-white mb-2">Create Invitation Link</h2>
            <p className="text-[#A1A1AA] mb-6">
              Generate a unique one-time link to invite a friend to the Beta program.
            </p>
            <Button
              onClick={createNewLink}
              disabled={creatingLink}
              className="btn-gold px-8 py-4"
            >
              {creatingLink ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2" size={18} />
                  Create Invitation Link
                </>
              )}
            </Button>
            <p className="text-[#52525B] text-sm mt-4">
              You can create {inviteData.invitations_remaining} more invitation{inviteData.invitations_remaining !== 1 ? 's' : ''}
            </p>
          </motion.div>
        )}

        {/* Existing Links */}
        {inviteData.invite_links && inviteData.invite_links.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-premium p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                <LinkIcon className="text-[#D4AF37]" size={20} />
              </div>
              <div>
                <h2 className="font-serif text-xl text-white">Your Invitation Links</h2>
                <p className="text-[#52525B] text-sm">Each link can only be used once</p>
              </div>
            </div>

            <div className="space-y-4">
              {inviteData.invite_links.map((link, index) => {
                const fullLink = `https://visionlab.clierxter.com/join?invite=${link.invite_code}`;
                return (
                  <div 
                    key={index} 
                    className={`p-4 border ${link.used ? 'bg-[#0A0A0A] border-[#1C1C1C] opacity-60' : 'bg-[#0A0A0A] border-[#D4AF37]/30'}`}
                  >
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 ${link.used ? 'bg-[#27272A] text-[#52525B]' : 'bg-[#D4AF37]/20 text-[#D4AF37]'}`}>
                            {link.used ? 'USED' : 'ACTIVE'}
                          </span>
                          <span className="text-[#52525B] text-xs">
                            Created {new Date(link.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-white font-mono text-sm truncate">{fullLink}</p>
                      </div>
                      {!link.used && (
                        <Button
                          onClick={() => copyToClipboard(fullLink, 'Link')}
                          className="bg-[#1C1C1C] hover:bg-[#27272A] text-white"
                          size="sm"
                        >
                          <Copy size={14} className="mr-1" />
                          Copy
                        </Button>
                      )}
                    </div>
                    
                    {!link.used && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => shareVia('email', fullLink)}
                          className="flex-1 flex items-center justify-center gap-1 p-2 bg-[#1C1C1C] hover:bg-[#27272A] text-white text-xs transition-colors"
                        >
                          <Mail size={14} />
                          Email
                        </button>
                        <button
                          onClick={() => shareVia('whatsapp', fullLink)}
                          className="flex-1 flex items-center justify-center gap-1 p-2 bg-[#1C1C1C] hover:bg-[#27272A] text-white text-xs transition-colors"
                        >
                          <MessageCircle size={14} />
                          WhatsApp
                        </button>
                        <button
                          onClick={() => shareVia('twitter', fullLink)}
                          className="flex-1 flex items-center justify-center gap-1 p-2 bg-[#1C1C1C] hover:bg-[#27272A] text-white text-xs transition-colors"
                        >
                          <Twitter size={14} />
                          Twitter
                        </button>
                        <button
                          onClick={() => shareVia('linkedin', fullLink)}
                          className="flex-1 flex items-center justify-center gap-1 p-2 bg-[#1C1C1C] hover:bg-[#27272A] text-white text-xs transition-colors"
                        >
                          <Linkedin size={14} />
                          LinkedIn
                        </button>
                      </div>
                    )}
                    
                    {link.used && link.used_at && (
                      <p className="text-[#52525B] text-xs">
                        Used on {new Date(link.used_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Invited Users List */}
        {inviteData.invited_users && inviteData.invited_users.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-premium p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                <Users className="text-[#D4AF37]" size={20} />
              </div>
              <div>
                <h2 className="font-serif text-xl text-white">Friends Who Joined</h2>
                <p className="text-[#52525B] text-sm">People who used your invitation links</p>
              </div>
            </div>

            <div className="space-y-3">
              {inviteData.invited_users.map((invitee, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1C1C1C]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1C1C1C] flex items-center justify-center text-[#D4AF37] font-serif">
                      {invitee.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-white">{invitee.name}</p>
                      <p className="text-[#52525B] text-sm">{invitee.email}</p>
                    </div>
                  </div>
                  {invitee.beta_access && (
                    <span className="text-xs text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1">BETA</span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-6"
        >
          <h3 className="text-[#E5C585] font-serif text-lg mb-3">How Invitations Work</h3>
          <ul className="space-y-2 text-[#A1A1AA] text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#D4AF37] mt-0.5" />
              <span>Crée un lien d'invitation unique (maximum {inviteData.max_invitations})</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#D4AF37] mt-0.5" />
              <span>Chaque lien ne peut être utilisé qu'une seule fois</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#D4AF37] mt-0.5" />
              <span>Ton amie utilise le lien pour s'inscrire à la Bêta</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#D4AF37] mt-0.5" />
              <span>Accès gratuit pendant la période Bêta pour toutes les invitées</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default InviteFriend;
